import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users, Bell, X, CheckCircle2 } from "lucide-react";

/**
 * Warteliste-Einschreibung — kann als Modal oder inline auf Vorstellungs-
 * Detail eingebettet werden, wenn die Vorstellung ausverkauft ist.
 *
 * Props:
 *   showtimeId, movieTitle, dateStr, showAsButton (default true)
 *   onEnrolled?: (entry) => void  — Callback nach erfolgreicher Einschreibung
 */
export default function WaitlistEnroll({ showtimeId, movieTitle, dateStr, showAsButton = true, onEnrolled }) {
  const [open, setOpen] = useState(!showAsButton);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [seatCount, setSeatCount] = useState(2);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Bitte Name eingeben");
    if (!email.trim()) return toast.error("E-Mail nötig für Benachrichtigung");
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist/enroll", {
        showtime_id: showtimeId,
        guest_name: name.trim(),
        guest_email: email.trim(),
        guest_phone: phone.trim() || null,
        seat_count: Number(seatCount) || 1,
        note: note.trim(),
      });
      setEnrolled(true);
      onEnrolled?.(data.entry);
      toast.success(data.already ? "Bereits eingetragen — du wirst benachrichtigt" : "In Warteliste eingetragen!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler bei Warteliste");
    } finally { setBusy(false); }
  };

  if (showAsButton && !open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="waitlist-open-btn"
        className="w-full inline-flex items-center justify-center gap-2 border border-[#EEDC82]/60 bg-[#EEDC82]/10 text-[#EEDC82] px-4 py-3 rounded uppercase tracking-widest text-xs hover:bg-[#EEDC82]/20">
        <Users size={14} /> Auf Warteliste setzen
      </button>
    );
  }

  return (
    <div className="card-lux p-5 relative" data-testid="waitlist-form">
      {showAsButton && (
        <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-[#F5F0E6]/60 hover:text-[#EE9CA1]">
          <X size={18} />
        </button>
      )}
      <div className="text-[10px] uppercase tracking-[0.35em] text-[#EEDC82] flex items-center gap-2"><Bell size={12} /> Warteliste</div>
      <h3 className="font-heading text-2xl mt-1">{enrolled ? "Eingetragen!" : "Wir informieren dich"}</h3>
      <p className="text-[11px] text-[#A8A398] mt-1">
        {enrolled
          ? "Sobald ein Platz frei wird, bekommst du eine E-Mail. Wer zuerst zurückbucht, sichert sich den Sitz."
          : `Diese Vorstellung ist voll. Trag dich in die Warteliste ein und wir schicken dir eine E-Mail, sobald jemand storniert.`}
      </p>
      <div className="text-[10px] text-[#575148] mt-1 font-mono">{movieTitle} · {dateStr}</div>

      {enrolled ? (
        <div className="mt-4 flex items-center gap-2 text-[#8FBC8F]">
          <CheckCircle2 size={20} />
          <span className="text-sm">Alles klar, wir melden uns bei dir.</span>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="waitlist-name"
              className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#EEDC82] outline-none" />
            <input type="email" placeholder="E-Mail *" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="waitlist-email"
              className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#EEDC82] outline-none" />
            <input type="tel" placeholder="Telefon (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
              data-testid="waitlist-phone"
              className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#EEDC82] outline-none" />
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Sitze</label>
              <input type="number" min="1" max="20" value={seatCount} onChange={(e) => setSeatCount(e.target.value)}
                data-testid="waitlist-seat-count"
                className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono focus:border-[#EEDC82] outline-none" />
            </div>
          </div>
          <input type="text" placeholder="Notiz (z. B. 'Nur vor 20 Uhr')" value={note} onChange={(e) => setNote(e.target.value)}
            data-testid="waitlist-note"
            className="w-full mt-2 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#EEDC82] outline-none" />
          <button onClick={submit} disabled={busy} data-testid="waitlist-submit"
            className="w-full mt-4 bg-[#EEDC82] hover:bg-[#F5E9A3] text-black py-3 rounded uppercase tracking-widest text-xs disabled:opacity-40 inline-flex items-center justify-center gap-2">
            <Users size={13} /> {busy ? "Trage ein …" : "Auf Warteliste setzen"}
          </button>
        </>
      )}
    </div>
  );
}
